import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { CheckCircle, Clock, User, ArrowRight, Home } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface CompletionStepProps {
	onBack: () => void;
}

export const CompletionStep = ({ onBack }: CompletionStepProps) => {
	const navigate = useNavigate();
	const { user } = useAuth();

	// Mark onboarding as completed when component mounts
	useEffect(() => {
		const markOnboardingComplete = async () => {
			if (user?.id) {
				try {
					const { data, error } = await supabase.functions.invoke(
						"update-user-onboarding",
						{
							body: {
								userId: user.id,
								onboardingCompleted: true,
								onboardingStep: "completed",
							},
						},
					);

					if (error) {
						console.error("Error marking onboarding complete:", error);
					}
				} catch (error) {
					console.error("Error updating user onboarding status:", error);
				}
			} else {
				console.error("No user ID available");
			}
		};

		markOnboardingComplete();
	}, [user?.id]);

	const handleGoToDashboard = () => {
		navigate("/doula-dashboard");
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className={styles.wrapperNarrow}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "100%" }} />
				</div>
				<p className={styles.progressNote}>Step 15 of 15</p>
			</div>

			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<CheckCircle className="h-6 w-6 mr-2 text-green-600" />
						Onboarding Complete!
					</CardTitle>
					<CardDescription className={styles.cardSubtitle}>
						Welcome to the doula community! Your profile is now under review.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="text-center">
						<div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
							<CheckCircle className="w-10 h-10 text-green-600" />
						</div>
						<h3 className="text-xl font-semibold mb-2 text-newTheme-black font-karla">
							Profile Submitted Successfully
						</h3>
						<p className="text-newTheme-black/60 font-karla">
							Thank you for completing your doula profile! Our team will review
							your information and activate your listing within 24-48 hours.
						</p>
					</div>

					<div className={styles.infoAlert}>
						<h4 className="font-medium text-newTheme-spruce mb-3 font-karla">
							What happens next:
						</h4>
						<div className="space-y-3">
							<div className="flex items-start space-x-3">
								<Clock className={`w-5 h-5 mt-0.5 ${styles.infoIcon}`} />
								<div className={styles.infoText}>
									<strong>Review Process:</strong> Our team will verify your
									credentials and information
								</div>
							</div>
							<div className="flex items-start space-x-3">
								<User className={`w-5 h-5 mt-0.5 ${styles.infoIcon}`} />
								<div className={styles.infoText}>
									<strong>Profile Activation:</strong> You'll receive an email
									when your profile goes live
								</div>
							</div>
							<div className="flex items-start space-x-3">
								<CheckCircle className={`w-5 h-5 mt-0.5 ${styles.infoIcon}`} />
								<div className={styles.infoText}>
									<strong>Start Receiving Bookings:</strong> Parents can find and
									book your services
								</div>
							</div>
						</div>
					</div>

					{/* Keep green for "in the meantime" — positive status color */}
					<div className="bg-green-50 border border-green-200 rounded-lg p-4">
						<h4 className="font-medium text-green-800 mb-2 font-karla">
							In the meantime:
						</h4>
						<ul className="text-sm text-green-700 space-y-1 list-disc list-inside font-karla">
							<li>Access your dashboard to manage your profile</li>
							<li>Update your availability calendar</li>
							<li>Review and edit your packages</li>
							<li>Prepare for your first client consultations</li>
						</ul>
					</div>

					<div className={styles.infoAlert}>
						<div className="flex items-start space-x-3">
							<Home className={`w-5 h-5 mt-0.5 ${styles.infoIcon}`} />
							<div className={styles.infoText}>
								<strong>Ready to get started?</strong> Access your dashboard to
								begin managing your doula business.
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			<div className={`${styles.navRow} mt-8`}>
				<Button
					variant="outline"
					onClick={onBack}
					className={styles.backButton}
				>
					Back
				</Button>
				<Button
					onClick={handleGoToDashboard}
					className={styles.submitButton}
				>
					<Home className="h-4 w-4 mr-2" />
					Go to Dashboard
					<ArrowRight className="h-4 w-4 ml-2" />
				</Button>
			</div>
		</motion.div>
	);
};
